/**
 * WidgetPanel — вкладка «Виджет на сайте».
 *
 * Зачем. Чат на сайте клиента ставится одной строкой, но строку эту надо где-то
 * взять. Раньше ключ виджета существовал только в базе Тони, и выдать его мог
 * лишь тот, у кого есть доступ к серверу. Здесь оператор копирует готовый
 * сниппет сам и сам же настраивает вид: заголовок, приветствие, цвет, фото.
 *
 * Ключ показываем, но менять не даём: сниппет уже стоит на сайте клиента, и
 * смена ключа молча погасила бы там чат. Перевыпуск — отдельная операция, и
 * пока она делается руками, чтобы случайный клик не отключил живой сайт.
 *
 * Фото ужимается прямо в браузере до 160×160: в реестр уходит data-URI, и он
 * едет к каждому посетителю сайта вместе с настройками — держим его лёгким.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Globe, Image as ImageIcon, Loader2 } from 'lucide-react'

import { agentsApi } from '@/api/client'
import { Button } from '@/components/ui/button'

const AVATAR_SIDE = 160
const AVATAR_QUALITY = 0.82

/** Пауза перед перерисовкой превью: иначе iframe моргает на каждой букве. */
const PREVIEW_DEBOUNCE_MS = 400

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

/** Адрес скрипта берём из готового сниппета — второго источника правды не заводим. */
function widgetSrcFromSnippet(snippet: string): string {
  return /src="([^"]+)"/.exec(snippet)?.[1] ?? ''
}

/**
 * Страница превью для iframe.
 *
 * Внутри — НАСТОЯЩИЙ скрипт виджета с настройками через data-атрибуты. Рисовать
 * окно чата второй раз силами React значило бы поддерживать две внешности,
 * которые неизбежно разъедутся: поправили отступ в виджете — превью врёт.
 *
 * `data-preview` отключает отправку: настоящий запрос отсюда завёл бы живой
 * диалог и мог бы создать заявку менеджеру.
 */
function previewDoc(opts: {
  src: string; open: boolean; title: string; greeting: string
  accent: string; avatar: string; quick: string[]
}): string {
  const attrs = [
    `src="${escapeAttr(opts.src)}${opts.src.includes('?') ? '&' : '?'}t=${Date.now()}"`,
    'data-preview="1"',
    opts.open ? 'data-open="1"' : '',
    `data-title="${escapeAttr(opts.title || 'Чат с консультантом')}"`,
    `data-greeting="${escapeAttr(opts.greeting)}"`,
    `data-accent="${escapeAttr(opts.accent)}"`,
    opts.avatar ? `data-avatar="${escapeAttr(opts.avatar)}"` : '',
    opts.quick.length ? `data-quick="${escapeAttr(opts.quick.join(' | '))}"` : '',
  ].filter(Boolean).join(' ')

  return `<!doctype html><html lang="ru"><head><meta charset="utf-8">
<style>
  html,body{margin:0;height:100%}
  body{background:#eef1f5;font:13px/1.4 -apple-system,Segoe UI,Roboto,sans-serif}
  .stub{padding:18px 20px;color:#98a2b3}
  .stub b{display:block;height:10px;width:120px;background:#dfe3ea;border-radius:5px;margin-bottom:10px}
  .stub i{display:block;height:8px;background:#e6eaf0;border-radius:4px;margin-bottom:7px}
</style></head><body>
<div class="stub"><b></b><i style="width:80%"></i><i style="width:65%"></i><i style="width:72%"></i></div>
<script ${attrs}></script>
</body></html>`
}

/** Файл из проводника → квадратный data-URI 160×160. */
async function toAvatarDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file)
  const side = Math.min(bitmap.width, bitmap.height)
  // Кадрируем по центру: портрет обычно снят вертикально, а нужен круг.
  const sx = (bitmap.width - side) / 2
  const sy = (bitmap.height - side) / 2
  const canvas = document.createElement('canvas')
  canvas.width = AVATAR_SIDE
  canvas.height = AVATAR_SIDE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Браузер не дал обработать изображение.')
  ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, AVATAR_SIDE, AVATAR_SIDE)
  return canvas.toDataURL('image/jpeg', AVATAR_QUALITY)
}

export function WidgetPanel({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['agent-widget', slug],
    queryFn: () => agentsApi.widget(slug),
  })

  const [title, setTitle] = useState('')
  const [greeting, setGreeting] = useState('')
  const [accent, setAccent] = useState('#2d6cdf')
  const [origins, setOrigins] = useState('')
  const [avatar, setAvatar] = useState('')
  const [quick, setQuick] = useState('')
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(true)

  // Превью отстаёт от полей на паузу: перерисовка iframe на каждой букве моргает.
  const [settled, setSettled] = useState(
    { title: '', greeting: '', accent: '', avatar: '', quick: '' })
  useEffect(() => {
    const id = setTimeout(() => setSettled({ title, greeting, accent, avatar, quick }),
      PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [title, greeting, accent, avatar, quick])

  useEffect(() => {
    if (!data?.exists) return
    setTitle(data.title || '')
    setGreeting(data.greeting || '')
    setAccent(data.accent || '#2d6cdf')
    setOrigins((data.allowed_origins || []).join('\n'))
    setAvatar(data.avatar || '')
    setQuick((data.quick_replies || []).join('\n'))
  }, [data])

  const save = useMutation({
    mutationFn: () =>
      agentsApi.saveWidget(slug, {
        title,
        greeting,
        accent,
        avatar,
        quick_replies: quick.split('\n').map((s) => s.trim()).filter(Boolean),
        allowed_origins: origins.split('\n').map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['agent-widget', slug] })
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'Не удалось сохранить.'),
  })

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Загружаем настройки виджета…
      </div>
    )
  }

  if (!data?.exists) {
    return (
      <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        Виджет для этого агента ещё не заведён. Напишите нам — выпустим ключ.
      </div>
    )
  }

  const openOrigins = origins.trim().length === 0

  return (
    <div className="space-y-4">
      {/* Главное на вкладке — строка для сайта. Поэтому она сверху и крупно. */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="text-sm font-medium">Код для сайта</div>
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          Вставьте эту строку перед закрывающим тегом <code>&lt;/body&gt;</code> —
          и чат появится в правом нижнем углу. Больше ничего подключать не нужно.
        </p>
        <div className="flex items-start gap-2">
          <code className="flex-1 overflow-x-auto rounded bg-muted px-3 py-2 text-[11px] leading-relaxed">
            {data.snippet}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              await navigator.clipboard.writeText(data.snippet)
              setCopied(true)
              setTimeout(() => setCopied(false), 1800)
            }}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            <span className="ml-1">{copied ? 'Скопировано' : 'Копировать'}</span>
          </Button>
        </div>
      </div>

      {/* Настройки слева, живое превью справа: правку видно сразу, без сохранения. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
      <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs font-medium">Заголовок чата</span>
          <input
            className="w-full rounded-md border px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например: Новый Вид"
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium">Цвет</span>
          <div className="flex items-center gap-2">
            <input
              type="color"
              className="h-9 w-12 rounded border p-1"
              value={/^#[0-9a-fA-F]{6}$/.test(accent) ? accent : '#2d6cdf'}
              onChange={(e) => setAccent(e.target.value)}
            />
            <input
              className="flex-1 rounded-md border px-3 py-2 text-sm"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              placeholder="#82b019"
            />
          </div>
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium">Первое сообщение в окне чата</span>
        <input
          className="w-full rounded-md border px-3 py-2 text-sm"
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
          placeholder="Здравствуйте! Напишите ваш вопрос — отвечу сразу."
        />
        <span className="block text-[11px] text-muted-foreground">
          Показывается сразу при открытии, до первого ответа бота. Не путать
          с приветствием из инструкции — то звучит в ответ на сообщение.
        </span>
      </label>

      {/* Плашки-подсказки */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="text-sm font-medium">Плашки под приветствием</div>
        <textarea
          className="h-20 w-full rounded-md border px-3 py-2 text-sm"
          value={quick}
          onChange={(e) => setQuick(e.target.value)}
          placeholder={'Сколько стоят потолки?\nСделать расчет\nЗаписаться на замер'}
        />
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          По одной на строке, до четырёх. Нажатие отправляет эту фразу боту —
          посетителю, который не знает, с чего начать, пустое поле ввода ничего
          не подсказывает, а готовый вопрос он нажмёт. После первого сообщения
          плашки пропадают: дальше разговор ведёт человек.
        </p>
      </div>

      {/* Фото */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <ImageIcon className="h-4 w-4 text-muted-foreground" /> Фото консультанта
        </div>
        <div className="flex items-center gap-3">
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="h-16 w-16 rounded-full object-cover ring-2 ring-border"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-[11px] text-muted-foreground">
              нет
            </div>
          )}
          <div className="space-y-1">
            <input
              type="file"
              accept="image/*"
              className="text-xs"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                try {
                  setAvatar(await toAvatarDataUrl(file))
                  setError(null)
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Не удалось прочитать файл.')
                }
              }}
            />
            <p className="text-[11px] text-muted-foreground">
              Квадрат обрежется сам. С фото чат читается как разговор с человеком.
            </p>
            {avatar && (
              <button
                type="button"
                className="text-[11px] text-muted-foreground underline"
                onClick={() => setAvatar('')}
              >
                убрать фото
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Домены */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Globe className="h-4 w-4 text-muted-foreground" /> Разрешённые сайты
        </div>
        <textarea
          className="h-20 w-full rounded-md border px-3 py-2 font-mono text-xs"
          value={origins}
          onChange={(e) => setOrigins(e.target.value)}
          placeholder={'newvid.ru\nwww.newvid.ru'}
        />
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          По одному домену в строке. Ключ виден в исходном коде страницы — это
          нормально, спрятать его нельзя. От переклейки на чужой сайт защищает
          именно этот список.
        </p>
        {openOrigins && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            Список пуст — чат ответит с любого сайта. Пока так, чужой может
            поставить этот код себе и расходовать ваш лимит. Впишите домен.
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
        {save.isSuccess && !save.isPending && (
          <span className="text-xs text-muted-foreground">
            Сохранено — на сайте обновится в течение минуты.
          </span>
        )}
      </div>
      </div>

      {/* Превью */}
      <div className="space-y-2 lg:sticky lg:top-4 lg:self-start">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium">Как это увидит посетитель</div>
          <div className="flex gap-1 rounded-md border p-0.5">
            <button
              type="button"
              onClick={() => setPreviewOpen(false)}
              className={`px-2 py-1 text-[11px] rounded ${
                previewOpen ? 'text-muted-foreground' : 'bg-accent font-medium'}`}
            >
              Свёрнутый
            </button>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className={`px-2 py-1 text-[11px] rounded ${
                previewOpen ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
            >
              Открытый
            </button>
          </div>
        </div>
        <iframe
          // key перезапускает страницу превью: скрипт виджета читает настройки
          // один раз при загрузке, менять их на лету он не умеет и не должен.
          key={`${previewOpen}|${settled.title}|${settled.greeting}|${settled.accent}|${settled.avatar.length}|${settled.quick}`}
          title="Превью виджета"
          className="w-full rounded-lg border bg-slate-100"
          style={{ height: previewOpen ? 660 : 200 }}
          sandbox="allow-scripts"
          srcDoc={previewDoc({
            src: widgetSrcFromSnippet(data.snippet),
            open: previewOpen,
            title: settled.title,
            greeting: settled.greeting,
            accent: settled.accent,
            avatar: settled.avatar,
            quick: settled.quick.split('\n').map((s) => s.trim()).filter(Boolean),
          })}
        />
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          Пульсация кнопки видна в режиме «Свёрнутый»: при открытом чате она
          гаснет, чтобы не отвлекала.
        </p>
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          Это тот же код, что встанет на сайт, — вид всегда совпадёт. Написать
          в превью можно, но ответ здесь показывается образцом: настоящий
          запрос завёл бы живой диалог и мог создать заявку менеджеру.
        </p>
      </div>
      </div>
    </div>
  )
}
